/**
 * Account Settings → Profile (#547 PR 5).
 *
 * Editable user-level fields:
 *   - Name
 *   - Email (with verification re-flow — see EmailChangeFlow below)
 *   - Timezone (stored on the user doc; not yet consumed by display
 *     code but persisted so future timezone-aware surfaces have a
 *     value to read)
 *
 * Avatar is deliberately not here — would require a new storage
 * surface (S3-equivalent + image-handling pipeline); deferred.
 *
 * Lifted out of the legacy `/account` single-page layout into a
 * Settings-shell tab. Existing /account redirects to
 * `/settings?tab=account.profile`.
 */
import { useState, type FormEvent } from 'react'
import { Button, Input, Label } from '@/components/ui'
import { authClient } from '@/auth/authClient'
import { useIdentity } from '@/hooks/useIdentity'
import { toUserMessage } from '@/lib/userMessage'

/** All IANA timezone identifiers exposed by the running runtime, e.g.
 * `["Africa/Abidjan", ..., "Pacific/Wallis"]`. Cached at module load
 * — the list doesn't change at runtime and the lookup is millisecond-
 * cheap on first call (Intl returns ~400 entries). */
const TIMEZONES: readonly string[] =
  typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : []

/** Browser-detected timezone — sensible default when the user has
 * never set one. */
function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

export function AccountProfile() {
  const { state, refreshIdentity } = useIdentity()

  if (state.status !== 'ready') return null
  const { user } = state.identity

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h3 className="text-base font-semibold">Profile</h3>
        <p className="text-sm text-muted-foreground">
          Your display name, email, and preferred timezone.
        </p>
      </div>
      <NameAndTimezoneForm
        user={user}
        onUpdated={refreshIdentity}
      />
      <EmailChangeForm
        currentEmail={user.email}
        onChangeStarted={refreshIdentity}
      />
    </div>
  )
}

// ── Name + timezone form ───────────────────────────────────────────────

function NameAndTimezoneForm({
  user,
  onUpdated,
}: {
  user: { name?: string; email: string; timezone?: string }
  onUpdated: () => Promise<void>
}) {
  const initialTimezone = user.timezone || browserTimezone()
  const [name, setName] = useState(user.name || '')
  const [timezone, setTimezone] = useState(initialTimezone)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const dirty =
    name.trim() !== (user.name || '') || timezone !== initialTimezone

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!dirty) {
      setSavedAt(Date.now())
      return
    }
    setSubmitting(true)
    try {
      const payload: { name?: string; timezone?: string } = {}
      if (name.trim() !== (user.name || '')) payload.name = name.trim()
      if (timezone !== initialTimezone) payload.timezone = timezone
      const { error: e } = await authClient.updateUser(
        payload as Parameters<typeof authClient.updateUser>[0],
      )
      if (e) throw new Error(e.message ?? 'Failed to save')
      await onUpdated()
      setSavedAt(Date.now())
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't save your profile. Try again — if the problem continues, check your connection.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="account-name">Name</Label>
        <Input
          id="account-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="account-timezone">Timezone</Label>
        <select
          id="account-timezone"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          disabled={submitting}
          className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
        >
          {!TIMEZONES.includes(timezone) && (
            <option value={timezone}>{timezone}</option>
          )}
          {TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          Used to render timestamps on your account; defaults to your
          browser's detected timezone ({browserTimezone()}).
        </p>
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {savedAt && !error && (
        <p className="text-sm text-success" key={savedAt}>
          Saved.
        </p>
      )}
      <Button type="submit" disabled={submitting || !dirty}>
        {submitting ? 'Saving…' : 'Save changes'}
      </Button>
    </form>
  )
}

// ── Email change flow ──────────────────────────────────────────────────

/** Email change requires a verification round-trip through the user's
 * inbox: clicking "Change email" doesn't immediately flip the email
 * on the user doc — Better Auth's `changeEmail` API queues the new
 * address, sends a verification link to the *new* address, and only
 * applies the change when that link is clicked. Until then,
 * `user.email` stays at the old value.
 *
 * We show the new-email input as a separate form to make the
 * round-trip distinction visually obvious. */
function EmailChangeForm({
  currentEmail,
  onChangeStarted,
}: {
  currentEmail: string
  onChangeStarted: () => Promise<void>
}) {
  const [newEmail, setNewEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = newEmail.trim().toLowerCase()
    if (!trimmed || !trimmed.includes('@')) {
      setError('Enter a valid email address.')
      return
    }
    if (trimmed === currentEmail.toLowerCase()) {
      setError('That\'s already your current email.')
      return
    }
    setSubmitting(true)
    try {
      const { error: e } = await authClient.changeEmail({
        newEmail: trimmed,
        callbackURL: '/settings?tab=account.profile',
      })
      if (e) throw new Error(e.message ?? 'Failed to start email change')
      setSentTo(trimmed)
      setNewEmail('')
      await onChangeStarted()
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't start the email change. Try again — if the problem continues, check your connection.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-3 border-t pt-6">
      <div>
        <Label className="text-sm font-medium">Email</Label>
        <p className="text-sm mt-1">
          {currentEmail}
          <span className="ml-2 text-xs text-muted-foreground">(current)</span>
        </p>
      </div>
      {sentTo && (
        <div className="rounded border border-success/40 bg-success/5 p-3 text-sm">
          We sent a verification link to <strong>{sentTo}</strong>. Click
          it to complete the change. Until then, your email stays at{' '}
          <strong>{currentEmail}</strong>.
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="new-email">New email</Label>
          <Input
            id="new-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={submitting}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" variant="outline" disabled={submitting || !newEmail.trim()}>
          {submitting ? 'Sending…' : 'Send verification to new email'}
        </Button>
      </form>
    </div>
  )
}
